import Swiper from 'swiper/bundle';
import { A11y, Navigation } from 'swiper/modules';
import { SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the Featured Content carousel module.
 * Supports client-side taxonomy-tab filtering with Swiper.
 */
export default class FeaturedContent extends MessageBus {
  /**
   * Constructor function of the FeaturedContent class. Must receive one argument,
   * a DOM node representing the featured content block element.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();

    /**
     * DOM node that is passed into the constructor
     * @property {HTMLElement} element DOM node passed into the constructor
     */
    this.element = element;

    /**
     * The currently active taxonomy filter slug.
     * 'all' means no filter is applied.
     * @property {string} activeFilter - The currently active taxonomy filter slug; 'all' means no filter is applied.
     */
    this.activeFilter = 'all';

    /**
     * Cache of every original slide node in DOM order.
     * Slides are detached and re-attached during filtering — never cloned —
     * so any child event listeners are preserved across tab switches.
     * @property {HTMLElement[]} allSlides - Array of original slide elements in DOM order.
     */
    this.allSlides = [];

    /**
     * The Swiper instance. Re-created each time a filter is applied.
     * @property {Swiper|null} slider - The active Swiper instance or `null` when not created.
     */
    this.slider = null;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners.
   * @returns {FeaturedContent} A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Find all necessary DOM elements used in the view and cache them.
   * @returns {FeaturedContent} A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.swiperElement = this.element.querySelector(
      SELECTORS.FEATURED_CONTENT_SWIPER
    );

    this.swiperWrapper = this.element.querySelector(
      SELECTORS.FEATURED_CONTENT_WRAPPER
    );

    this.tabs = Array.from(
      this.element.querySelectorAll(SELECTORS.FEATURED_CONTENT_TAB)
    );

    this.prevButton = this.element.querySelector(
      SELECTORS.FEATURED_CONTENT_PREV
    );

    this.nextButton = this.element.querySelector(
      SELECTORS.FEATURED_CONTENT_NEXT
    );

    // Snapshot slides before Swiper mutates the wrapper
    if (this.swiperWrapper) {
      this.allSlides = Array.from(this.swiperWrapper.children);
    }

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {FeaturedContent} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleTabClick = this.handleTabClick.bind(this);
    this.handleTabKeydown = this.handleTabKeydown.bind(this);

    return this;
  }

  /**
   * Create event listeners to enable interaction with the view.
   * @returns {FeaturedContent} A reference to the current instance of the class
   * @chainable
   */
  enable() {
    if (!this.swiperElement || !this.swiperWrapper) return this;

    this.createSlider();
    this.bindTabEvents();
    this.bindKeyboardEvents();

    // Activate the first tab on initial load
    if (this.tabs.length) {
      const firstTab = this.tabs[0];
      this.activeFilter = firstTab.dataset.filter;
      firstTab.classList.add('is-active');
      firstTab.setAttribute('aria-selected', 'true');

      // Only re-filter if the first tab isn't 'all' (slides are already fully shown)
      if (this.activeFilter !== 'all') {
        this.filterSlides(this.activeFilter);
      }
    }

    return this;
  }

  // ---------------------------------------------------------------------------
  // Slider
  // ---------------------------------------------------------------------------

  /**
   * Instantiates (or re-instantiates) the Swiper carousel.
   * Called once on init and again each time a filter tab is activated,
   * so Swiper recalculates dimensions against the new slide set.
   *
   * NOTE: `slidesPerView: 'auto'` means Swiper reads each slide's real
   * rendered width from CSS. Combined with `centeredSlides`, the active
   * slide is centered exactly at any viewport — so the CSS `max-width`
   * cap on the slide is honored without knocking it off-center.
   * The amount of peek is controlled by the slide width in the SCSS.
   *
   * Any width cap MUST live on `.featured-content__slide`, never on
   * `.featured-content__card`. Swiper measures and centers the slide box;
   * capping the card instead leaves a full-width slide with a narrower card
   * sitting inside it, which reads as an off-center card.
   * @returns {FeaturedContent} A reference to the current instance of the class
   * @chainable
   */
  createSlider() {
    if (this.slider && !this.slider.destroyed) {
      this.slider.destroy(true, true);
      this.slider = null;
    }

    // If only one slide is visible after filtering, disable navigation
    const slideCount = this.swiperWrapper.children.length;
    if (this.prevButton) this.prevButton.disabled = slideCount <= 1;
    if (this.nextButton) this.nextButton.disabled = slideCount <= 1;

    this.slider = new Swiper(this.swiperElement, {
      modules: [A11y, Navigation],
      direction: 'horizontal',
      slidesPerView: 'auto',
      centeredSlides: true,
      spaceBetween: 16,
      grabCursor: true,
      watchSlidesProgress: true,
      navigation: {
        prevEl: this.prevButton,
        nextEl: this.nextButton
      },
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous slide',
        nextSlideMessage: 'Next slide'
      },
      breakpoints: {
        640: {
          slidesPerView: 'auto',
          spaceBetween: 24
        },
        1024: {
          slidesPerView: 'auto',
          spaceBetween: 32,
          centeredSlides: true
        },
        1280: {
          slidesPerView: 'auto',
          spaceBetween: 40,
          centeredSlides: true
        }
      }
    });

    this.slider.on('init', () => this.syncNavState());
    this.slider.on('slideChange', () => this.syncNavState());
    this.slider.on('reachBeginning', () => this.syncNavState());
    this.slider.on('reachEnd', () => this.syncNavState());

    return this;
  }

  // ---------------------------------------------------------------------------
  // Navigation state
  // ---------------------------------------------------------------------------

  /**
   * Reflects Swiper's beginning/end state onto the prev/next button
   * disabled attributes so CSS can style them correctly.
   * @returns {FeaturedContent} A reference to the current instance of the class
   * @chainable
   */
  syncNavState() {
    if (!this.slider) return this;

    if (this.prevButton) this.prevButton.disabled = this.slider.isBeginning;
    if (this.nextButton) this.nextButton.disabled = this.slider.isEnd;

    return this;
  }

  // ---------------------------------------------------------------------------
  // Taxonomy filtering
  // ---------------------------------------------------------------------------

  /**
   * Filters the carousel to only show slides whose `data-terms` attribute
   * contains the given slug, then rebuilds the Swiper instance.
   * Passing 'all' restores every slide.
   * @param {string} filter - Taxonomy term slug or 'all'
   * @returns {FeaturedContent} A reference to the current instance of the class
   * @chainable
   */
  filterSlides(filter) {
    const filtered =
      filter === 'all'
        ? this.allSlides
        : this.allSlides.filter((slide) => {
            const terms = (slide.dataset.terms || '')
              .split(' ')
              .filter(Boolean);
            return terms.includes(filter);
          });

    // Detach every slide from the wrapper (keeps nodes in memory — no cloning)
    this.allSlides.forEach((slide) => {
      if (slide.parentNode === this.swiperWrapper) {
        this.swiperWrapper.removeChild(slide);
      }

      // Strip leftover Swiper inline styles so the layout recalculates cleanly
      slide.removeAttribute('style');
      slide.classList.remove(
        'swiper-slide-active',
        'swiper-slide-next',
        'swiper-slide-prev',
        'swiper-slide-visible'
      );
    });

    // Re-attach only the matching slides
    filtered.forEach((slide) => this.swiperWrapper.appendChild(slide));

    // Rebuild Swiper against the new slide set
    this.createSlider();

    return this;
  }

  // ---------------------------------------------------------------------------
  // Tab click
  // ---------------------------------------------------------------------------

  /**
   * Handles a click on any taxonomy filter tab.
   * Skips the update if the clicked tab is already active.
   * @param {MouseEvent} event - The mouse click event fired on the tab button.
   */
  handleTabClick(event) {
    const tab = event.currentTarget;
    const { filter } = tab.dataset;

    // Toggle off: clicking the active tab resets to 'all'
    if (filter === this.activeFilter) {
      this.activeFilter = 'all';

      this.tabs.forEach((t) => {
        t.classList.remove('is-active');
        t.setAttribute('aria-selected', 'false');
      });

      tab.blur(); // Drop focus ring after deselection

      this.filterSlides('all');
      return;
    }

    this.activeFilter = filter;

    this.tabs.forEach((t) => {
      t.classList.remove('is-active');
      t.setAttribute('aria-selected', 'false');
    });

    tab.classList.add('is-active');
    tab.setAttribute('aria-selected', 'true');

    this.filterSlides(filter);
  }

  /**
   * Adds click listeners to every tab button.
   * @returns {FeaturedContent} A reference to the current instance of the class
   * @chainable
   */
  bindTabEvents() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.handleTabClick);
    });

    return this;
  }

  // ---------------------------------------------------------------------------
  // Keyboard navigation (ARIA tablist pattern)
  // ---------------------------------------------------------------------------

  /**
   * Handles keydown events on tab buttons, implementing the ARIA tablist
   * roving-focus pattern (ArrowLeft / ArrowRight / Home / End).
   * @param {KeyboardEvent} event - The keyboard event fired on the focused tab button.
   */
  handleTabKeydown(event) {
    const currentIndex = this.tabs.indexOf(event.currentTarget);
    const total = this.tabs.length;
    let targetIndex = -1;

    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        targetIndex = (currentIndex + 1) % total;
        break;

      case 'ArrowLeft':
        event.preventDefault();
        targetIndex = (currentIndex - 1 + total) % total;
        break;

      case 'Home':
        event.preventDefault();
        targetIndex = 0;
        break;

      case 'End':
        event.preventDefault();
        targetIndex = total - 1;
        break;

      default:
        return;
    }

    if (targetIndex >= 0) {
      this.tabs[targetIndex].focus();
      this.tabs[targetIndex].click();
    }
  }

  /**
   * Adds keydown listeners to every tab button for keyboard navigation.
   * @returns {FeaturedContent} A reference to the current instance of the class
   * @chainable
   */
  bindKeyboardEvents() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('keydown', this.handleTabKeydown);
    });

    return this;
  }
}
